import {
  fmt,
  findDuplicateName,
  parseKip,
  searchCustomers,
  type Customer,
  type NewCustomer,
} from '@k2pos/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from './Button';
import { Field } from './Field';
import { Mono, Text } from './Text';
import { IconCheck, IconPlus } from '@/components/icons';
import { cx } from '@/lib/cx';
import { colors } from '@/theme/tokens';
import styles from './CustomerSheet.module.css';

interface CustomerSheetProps {
  open: boolean;
  customers: Customer[];
  selectedId: string | null;
  busy?: boolean;
  onSelect: (customerId: string | null) => void;
  onCreate: (input: NewCustomer) => void;
  onClose: () => void;
}

/**
 * Picks or creates the customer a sale is for.
 *
 * Replaces a horizontal strip of every customer, which was fine for the three
 * seeded ones and unusable at the two dozen a real shop carries. Creating from
 * here matters because the alternative — leaving the counter to add someone
 * before you can sell to them on credit — is exactly when a shopkeeper gives up
 * and records the debt on paper.
 */
export function CustomerSheet({
  open,
  customers,
  selectedId,
  busy = false,
  onSelect,
  onCreate,
  onClose,
}: CustomerSheetProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [limit, setLimit] = useState('');
  const [confirmedDuplicate, setConfirmedDuplicate] = useState(false);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      setQuery('');
      setCreating(false);
      setName('');
      setPhone('');
      setLimit('');
      setConfirmedDuplicate(false);
      dialog.showModal();
    }
    if (!open && dialog.open) dialog.close();
  }, [open]);

  const results = useMemo(() => searchCustomers(customers, query), [customers, query]);

  if (!open) return null;

  const duplicate = name.trim() ? findDuplicateName(customers, name) : undefined;
  const canCreate = name.trim().length > 0 && (!duplicate || confirmedDuplicate);

  function startCreating() {
    setName(query.trim());
    setCreating(true);
  }

  return (
    <dialog
      ref={ref}
      className={styles.dialog}
      onClose={onClose}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div className={styles.sheet}>
        <div className={styles.grabber} />

        {creating ? (
          <>
            <Text weight={800} size={16}>
              ເພີ່ມລູກຄ້າໃໝ່
            </Text>

            <div className={styles.field}>
              <Text weight={700} size={11} color={colors.textMuted}>
                ຊື່
              </Text>
              <Field value={name} onChange={setName} placeholder="ເຊັ່ນ ຊ່າງ ຄຳ" label="ຊື່ລູກຄ້າ" autoFocus />
            </div>

            {/* Warn, do not block: two people really can share a name, but two
                identical rows in a debt book means payments land on the wrong
                one, so the shopkeeper decides. */}
            {duplicate ? (
              <button
                type="button"
                onClick={() => setConfirmedDuplicate((v) => !v)}
                className={cx(styles.duplicate, confirmedDuplicate && styles.duplicateOn)}
              >
                <span className={cx(styles.tick, confirmedDuplicate && styles.tickOn)}>
                  {confirmedDuplicate ? <IconCheck size={12} /> : null}
                </span>
                <div className="grow col">
                  <Text weight={700} size={12} align="left" color={colors.navy}>
                    ມີ “{duplicate.name}” ຢູ່ແລ້ວ · ຕິດ {fmt(duplicate.balance)} ₭
                  </Text>
                  <Text size={10.5} color={colors.textMuted} align="left">
                    ຖ້າແມ່ນຄົນດຽວກັນ ໃຫ້ກົດຍົກເລີກ ແລ້ວເລືອກຈາກລາຍຊື່ · ຖ້າຄົນລະຄົນ ໃຫ້ຕິກນີ້
                  </Text>
                </div>
              </button>
            ) : null}

            <div className={styles.field}>
              <Text weight={700} size={11} color={colors.textMuted}>
                ເບີໂທ · ບໍ່ບັງຄັບ
              </Text>
              <Field value={phone} onChange={setPhone} kind="tel" placeholder="020 5512 3480" label="ເບີໂທ" />
            </div>

            <div className={styles.field}>
              <Text weight={700} size={11} color={colors.textMuted}>
                ວົງເງິນ · ບໍ່ບັງຄັບ
              </Text>
              <Field value={limit} onChange={setLimit} kind="kip" placeholder="5000000" label="ວົງເງິນ" />
            </div>

            <div className={styles.actions}>
              <Button
                label="ຍົກເລີກ"
                variant="outline"
                className="grow"
                onPress={() => setCreating(false)}
              />
              <Button
                label={busy ? 'ກຳລັງບັນທຶກ...' : 'ບັນທຶກ'}
                className="grow"
                disabled={!canCreate || busy}
                onPress={() =>
                  onCreate({
                    name,
                    phone,
                    limit: parseKip(limit) ?? 0,
                  })
                }
              />
            </div>
          </>
        ) : (
          <>
            <Text weight={800} size={16}>
              ເລືອກລູກຄ້າ
            </Text>

            <Field
              value={query}
              onChange={setQuery}
              placeholder="ຄົ້ນຫາຊື່ ຫຼື ເບີໂທ..."
              label="ຄົ້ນຫາລູກຄ້າ"
              autoFocus
            />

            <div className={styles.list}>
              {/* Pinned: a walk-in is the commonest case and must never be
                  scrolled to. */}
              <button
                type="button"
                onClick={() => onSelect(null)}
                className={cx(styles.row, selectedId === null && styles.rowOn)}
              >
                <div className="grow col">
                  <Text weight={700} size={13.5} align="left">
                    ລູກຄ້າທົ່ວໄປ
                  </Text>
                  <Text size={10.5} color={colors.textMuted} align="left">
                    ຈ່າຍເລີຍ · ບໍ່ຕິດໜີ້
                  </Text>
                </div>
                {selectedId === null ? <IconCheck size={16} className={styles.check} /> : null}
              </button>

              {results.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => onSelect(c.id)}
                  className={cx(styles.row, c.id === selectedId && styles.rowOn)}
                >
                  <div className="grow col">
                    <Text weight={700} size={13.5} lines={1} align="left">
                      {c.name}
                    </Text>
                    <Mono size={10.5} color={colors.textMuted} align="left">
                      {c.phone || '—'}
                      {c.balance > 0 ? ` · ຕິດ ${fmt(c.balance)} ₭` : ''}
                    </Mono>
                  </div>
                  {c.id === selectedId ? <IconCheck size={16} className={styles.check} /> : null}
                </button>
              ))}

              {results.length === 0 ? (
                <Text size={12} color={colors.textMuted} align="center" className={styles.empty}>
                  ບໍ່ພົບ “{query.trim()}”
                </Text>
              ) : null}
            </div>

            <button type="button" onClick={startCreating} className={styles.addRow}>
              <IconPlus size={16} />
              <Text weight={700} size={13} color={colors.navy}>
                {query.trim() ? `ເພີ່ມລູກຄ້າໃໝ່ “${query.trim()}”` : 'ເພີ່ມລູກຄ້າໃໝ່'}
              </Text>
            </button>
          </>
        )}
      </div>
    </dialog>
  );
}
